import { Modal } from 'components/Modal';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import filter from 'lodash/filter';
import { intersection } from 'lodash';
import { ModalBody } from 'components/Modal/ModalBody';
import { TextField } from 'components/TextField';
import map from 'lodash/map';
import { Checkbox } from 'components/Checkbox';
import includes from 'lodash/includes';
import { Box } from 'components/Box';
import { ModalActions } from 'components/Modal/ModalActions';
import { Button } from 'components/Button';
import { ArrowLeftLongIcon } from 'icons/outline';
import noop from 'lodash/noop';
import { HiringUpdateFlowContext } from 'features/EmployerHiringUpdateFlow/EmployerHiringUpdateContext';
import { HiringUpdateFlowSteps } from 'features/EmployerHiringUpdateFlow/EmployerHiringUpdateFlow.constants';
import { InputInstructions } from 'features/EmployerHiringUpdateFlow/InputInstructions';

const PROVIDER_OPTIONS = [
  'Indeed',
  'LinkedIn',
  'Monster',
  'Career Builder',
  'Snag a Job',
  'Craigslist',
  'Referral (from staff, parent, friend)',
  'Facebook Groups',
  'My own website',
  'Through a college or university',
  'Hired a substitute or temporary teacher',
  'Childcare Careers (childcarecareers.com)',
  'Childcare Staffing (childcarestaffing.com)',
];

export const HiringMethodsStep = () => {
  const {
    open,
    step,
    goToNextStep,
    goToPreviousStep,
    thirdPartyProviders,
    setThirdPartyProviders,
  } = useContext(HiringUpdateFlowContext);

  const [showOtherTextField, setShowOtherTextField] = useState(false);
  const [otherTextFieldValue, setOtherTextFieldValue] = useState('');

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;
    setThirdPartyProviders(
      checked
        ? [...thirdPartyProviders, value]
        : filter(thirdPartyProviders, (item) => item !== value),
    );
  };

  const handleOtherChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;

    if (!checked) {
      setOtherTextFieldValue('');
      setThirdPartyProviders((prev) => intersection(prev, PROVIDER_OPTIONS));
    } else {
      setThirdPartyProviders((prev) => [...prev, otherTextFieldValue]);
    }

    setShowOtherTextField(checked);
  };

  const handleOtherTextFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    const selectedOptions = intersection(PROVIDER_OPTIONS, thirdPartyProviders);

    setOtherTextFieldValue(value);
    setThirdPartyProviders([...selectedOptions, value]);
  };

  useEffect(() => {
    if (!open) {
      setShowOtherTextField(false);
      setOtherTextFieldValue('');
    }
  }, [open]);

  return (
    <Modal
      panelProps={{
        className: 'sm:!max-w-[476px]',
      }}
      open={open && step === HiringUpdateFlowSteps.WHICH_THIRD_PARTY_PLATFORMS}
      heading={
        <span className="text-primary-pure-blue">
          What methods did you use to hire outside EarlyDay?
        </span>
      }
      actionRequired
      onClose={noop}
    >
      <ModalBody>
        <InputInstructions>Select all that apply.</InputInstructions>

        <Box className="mt-6 !py-4">
          <div className="flex flex-col gap-2">
            {map(PROVIDER_OPTIONS, (providerName) => (
              <div key={providerName} className="flex-none">
                <Checkbox
                  id={providerName}
                  checked={includes(thirdPartyProviders, providerName)}
                  onChange={handleChange}
                  value={providerName}
                  text={
                    <span className="text-sm text-neutral-dark whitespace-normal">
                      {providerName}
                    </span>
                  }
                />
              </div>
            ))}

            <Checkbox
              id="other"
              checked={showOtherTextField}
              value="Other"
              onChange={handleOtherChange}
              text={<span className="text-neutral-dark">Other</span>}
            />

            {(showOtherTextField || null) && (
              <div className="px-2">
                <TextField
                  placeholder="Tell us how you hired"
                  value={otherTextFieldValue}
                  onChange={handleOtherTextFieldChange}
                />
              </div>
            )}
          </div>
        </Box>
      </ModalBody>

      <ModalActions>
        <Button
          type="button"
          size="lg"
          fullWidth
          onClick={goToNextStep}
          disabled={
            (showOtherTextField && !otherTextFieldValue) ||
            (!showOtherTextField && !thirdPartyProviders.length)
          }
        >
          Confirm
        </Button>
        <Button type="button" size="lg" variant="outline" onClick={goToPreviousStep} fullWidth>
          <ArrowLeftLongIcon className="mr-2" />
          <span>Go back</span>
        </Button>
      </ModalActions>
    </Modal>
  );
};
